import type {
  NextDayPricePredictionPayload,
  PriceIntelligenceFeedPayload,
} from '@/services/api';

type FeedRequest = {
  date: string;
  ohlc?: {
    open?: number;
    high?: number;
    low?: number;
    close: number;
    volume?: number;
    pct_change?: number;
  };
};

function fmtUsd(price: number): string {
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPct(pct: number): string {
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

export function buildMockPriceIntelligenceFeed(
  ticker: string,
  body: FeedRequest,
): PriceIntelligenceFeedPayload {
  const symbol = ticker.toUpperCase();
  const close = body.ohlc?.close ?? 42.18;
  const pct = body.ohlc?.pct_change ?? 2.34;
  const high = body.ohlc?.high ?? close * 1.012;
  const low = body.ohlc?.low ?? close * 0.986;
  const volM = ((body.ohlc?.volume ?? 1_240_000) / 1_000_000).toFixed(2);

  return {
    success: true,
    symbol,
    date: body.date,
    headline: `${symbol} finished ${fmtPct(pct)} on ${body.date.replace(/-/g, '.')} — buyers defended the open dip and pressed into the close.`,
    items: [
      {
        id: 'open-read',
        kind: 'read',
        title: 'Opening tone',
        body: `Gap held above prior support. First 30 minutes showed passive selling into ${fmtUsd(low)} before buyers stepped in on declining offer size.`,
        sentiment: pct >= 0 ? 'bullish' : 'bearish',
        confidence: 'medium',
      },
      {
        id: 'tape-midday',
        kind: 'tape',
        title: 'Midday tape',
        body: `Volume paced at ~${volM}M shares with buy-side aggression clustering between ${fmtUsd(low)} and ${fmtUsd((low + close) / 2)}. Repeated sweeps of the offer without follow-through selling suggests short-covering plus incremental long adds.`,
        sentiment: 'bullish',
        confidence: 'high',
      },
      {
        id: 'context-catalyst',
        kind: 'context',
        title: 'Context',
        body: 'Sector peers traded in line with semis; no idiosyncratic headline flow. Move reads as positioning adjustment ahead of the next data print rather than a fundamental re-rating.',
        sentiment: 'neutral',
        confidence: 'medium',
      },
      {
        id: 'outlook-close',
        kind: 'outlook',
        title: 'Close',
        body: `Session resolved near ${fmtUsd(close)} (${fmtPct(pct)}), holding above VWAP into the bell. Risk is a failed retest of ${fmtUsd(high)} on lighter volume tomorrow.`,
        sentiment: pct >= 0 ? 'bullish' : 'neutral',
        confidence: 'medium',
      },
    ],
    generated_at: new Date().toISOString(),
    model: 'mock-preview',
    sources_used: [
      'chart_ohlc',
      'chart_recent_bars',
      'trade_session_stats',
      'day_narrative',
    ],
    from_cache: true,
  };
}

export function buildMockNextDayPricePrediction(
  ticker: string,
  asOfDate: string,
): NextDayPricePredictionPayload {
  const symbol = ticker.toUpperCase();

  return {
    success: true,
    symbol,
    as_of_date: asOfDate,
    target_date: asOfDate,
    generated_at: new Date().toISOString(),
    model: 'mock-preview',
    from_cache: true,
    market_view: {
      market_state: 'Markup — higher lows, acceptance above prior range mid',
      thesis_confidence_pct: 68,
      current_thesis: `${symbol} is consolidating gains after a constructive close. As long as overnight liquidity does not gap below the session VWAP, the path of least resistance skews toward a retest of the recent swing high with participation on the open.`,
      invalidation: 'A decisive break below the prior day low on expanding sell volume would flip the read to distribution and favor a fade back into the weekly value area.',
      next_likely_move:
        'Base case: modest gap-up or flat open, probe of resistance in the first hour, then range extension if buy imbalance persists into midday.',
    },
    prediction: {
      reference_date: asOfDate,
      direction: 'up',
      confidence: 'medium',
      predicted_change_pct: 0.85,
    },
  };
}
